/* eslint-disable no-console */
import { captureException, captureMessage } from '@sentry/react';
import { type ScopeContext } from '@sentry/types';

import { UNEXPECTED_RESPONSE_FINGERPRINT } from './unexpectedResponse';

const isProduction = process.env.NODE_ENV !== 'development';
const isTest = process.env.NODE_ENV === 'test';

type LoggingHelper = (
  message: string | Error | ErrorEvent,
  context?: Partial<ScopeContext>
) => void;

/* istanbul ignore next */
export const warn: LoggingHelper = (message, context) => {
  if (isProduction) {
    captureMessage(message.toString(), {
      ...(context || {}),
      level: 'warning',
    });
  } else if (!isTest) {
    console.warn('%o', message, context);
  }
};

/* istanbul ignore next */
export const error: LoggingHelper = (message, context) => {
  if (isProduction) {
    captureException(message, context);
  }
  if (!isTest) {
    console.error('%o', message, context);
  }
};

// Proportion of errors being reported
// Adjust, higher if we fix errors and end up not maxing out our quota
export const ERROR_SAMPLE_RATE = 0.005; // temporary

// Reported every time, not sampled: these count the users affected
const unsampledFingerprints: string[] = [UNEXPECTED_RESPONSE_FINGERPRINT];

/**
 * Sentry's beforeSend: samples errors here rather than with its sampleRate,
 * which would drop them before any exemption could apply
 */
export const sampleErrors = <E extends { fingerprint?: string[] }>(
  event: E
): E | null =>
  event.fingerprint?.some((part) => unsampledFingerprints.includes(part)) ||
  Math.random() < ERROR_SAMPLE_RATE
    ? event
    : null;

/* istanbul ignore next */
export const debug: LoggingHelper = (message, context) => {
  if (!isProduction && !isTest) {
    console.debug('%o', message, context);
  }
};
